import posthog, { type CaptureResult, type PostHogConfig } from "posthog-js";
import type { BrowserAnalytics, HandbookAskResponse, Theme, Tool } from "@carbon-os/server/contract";

/**
 * Usage analytics in the browser, sent to the PostHog project several Carbon
 * apps share. Nothing loads until the server hands over settings with a 200 for
 * somebody in the staff directory: no settings, no PostHog, no `ph_*` cookie.
 *
 * Sent: who is signed in (identify), a `$pageview` per page, a `tool_clicked`
 * per tool opened, and the app, training and handbook events below. Everything
 * else is switched off in code, so another app's owner cannot switch it on from
 * the shared project's settings.
 *
 * Every event and its properties (all ids, enums, counts, versions or durations;
 * never typed text, a title, a folder's name or a URL):
 *
 * - `$pageview` {page}: `my_tools`, `training`, `handbook`, `handbook_document`,
 *   `sign_in` or `sign_out`; one when PostHog starts, then one per path change.
 * - `tool_clicked` {tool_key, tool_name, tool_kind, via: card | filter_enter}
 * - `theme_changed` {theme: light | dark}
 * - `sign_out_clicked` {via: sign_out | switch_account}
 * - `tools_filtered` {visible_count, total_count}, once the filter settles
 * - `it_ticket_clicked` {via: missing_tool | no_tools | tool_unavailable, tool_key?}
 * - `retry_clicked` {area}, `signin_again_clicked` {area}: see `RetryArea` and `SignInArea`
 * - `training_viewed` {track_count, items_done, items_total}
 * - `training_item_opened` {material_id, track_id, kind: file | video | process}
 * - `training_item_completed`, `training_item_uncompleted` {material_id, track_id}
 * - `training_track_completed` {track_id, item_count}
 * - `training_nudge_clicked` {track_id}
 * - `training_player_opened` {track_id, material_id, step_kind, via: start | continue | row | quiz_row | link}
 * - `training_step_selected` {track_id, material_id, step_kind, via: sidebar | next_button}
 * - `training_player_closed` {track_id, duration_ms}
 * - `training_process_acknowledged` {material_id, track_id, outcome: saved | track_expired | version_changed | error}
 * - `training_quiz_started` {material_id, track_id, resumed, answered, total}
 * - `training_quiz_submitted` {material_id, track_id, passed, percent, correct, total, attempt}
 * - `training_quiz_failed_to_save` {material_id, status}
 * - `training_quiz_closed` {material_id, track_id, stage: loading | answering | result | unavailable, answered, total}
 * - `training_video_opened` {material_id, track_id, resumed}
 * - `training_video_completed` {material_id, track_id}
 * - `training_video_error` {material_id, code, status}
 * - `handbook_tab_changed` {tab, from: ask | browse}
 * - `handbook_searched` {result_count, document_count}, once the search settles
 * - `handbook_folder_opened` {folder_id: id | unfiled | null, via: sidebar | card | breadcrumb | back}
 * - `handbook_document_opened` {document_id, via, position, in_book, book_position,
 *   book_page_count, updated_since_read, outcome: shown | not_available | error}
 * - `handbook_book_finished` {folder_id, page_count}
 * - `handbook_document_marked_read` {document_id, version, reread, in_book, outcome: saved | newer_version | error}
 * - `handbook_question_asked` {outcome, source_count, duration_ms, retry}
 * - `handbook_citation_opened` {document_id, position}
 * - `handbook_passage_opened` {document_id, position, kind: related | matching}
 * - `handbook_answer_cleared` {outcome}
 *
 * Every event carries `app` and `environment`, both from the server's settings,
 * stamped in `before_send` rather than by register(): reset() clears registered
 * properties.
 */

/**
 * Keeps a URL's origin and path. The query and fragment always go: an SPA with
 * one route has nothing in them worth a metric, and a sign-in redirect can put a
 * token there. Sentinels such as `$direct` ("no referrer") pass through, or
 * Direct attribution breaks.
 */
export function sanitizeUrl(raw: string): string {
  if (!raw) return raw;
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  if (!absolute && !raw.startsWith("/")) return raw;

  // A path resolves against a placeholder origin that is cut off again after; a
  // protocol-relative `//host/path` names its own host, so only a scheme is lent.
  const prefix = absolute ? "" : raw.startsWith("//") ? "https:" : "http://x.invalid";
  let url: URL;
  try {
    url = new URL(prefix + raw);
  } catch {
    // Not a URL we can inspect, so report nothing rather than something.
    return "";
  }
  url.search = "";
  url.hash = "";
  return url.toString().slice(prefix.length);
}

/** Property keys whose values are URLs. `referring_domain` is only a host. */
function isUrlKey(key: string): boolean {
  return /url|pathname|referrer/i.test(key) && !/referring_domain/i.test(key);
}

/** Sanitises URL-bearing properties in place, including inside nested objects and arrays. */
export function sanitizeProperties(props: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === "string" && isUrlKey(key)) {
      props[key] = sanitizeUrl(value);
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (item && typeof item === "object") sanitizeProperties(item as Record<string, unknown>);
      }
    } else if (value && typeof value === "object") {
      sanitizeProperties(value as Record<string, unknown>);
    }
  }
}

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const HTTP_URL = /https?:\/\/[^\s"'<>()]+/gi;

/**
 * Error text can quote the address on screen or the page's URL, and a sign-in
 * redirect can leave a token in that URL's query.
 */
export function maskErrorText(text: string): string {
  return text.replace(HTTP_URL, (url) => sanitizeUrl(url)).replace(EMAIL, "[email]");
}

/**
 * An `$exception` names URLs outside any `*url*` key: in its messages, and as a
 * stack frame's file when the code ran from the page itself.
 */
function scrubException(props: Record<string, unknown>): void {
  if (typeof props["$exception_message"] === "string") {
    props["$exception_message"] = maskErrorText(props["$exception_message"]);
  }
  const list = props["$exception_list"];
  if (!Array.isArray(list)) return;
  for (const entry of list) {
    if (!entry || typeof entry !== "object") continue;
    if (typeof entry.value === "string") entry.value = maskErrorText(entry.value);
    const frames = entry.stacktrace?.frames;
    if (!Array.isArray(frames)) continue;
    for (const frame of frames) {
      if (!frame || typeof frame !== "object") continue;
      for (const key of ["filename", "abs_path"]) {
        if (typeof frame[key] === "string" && /^https?:/i.test(frame[key])) {
          frame[key] = sanitizeUrl(frame[key]);
        }
      }
    }
  }
}

/**
 * The last thing every event passes through. posthog-js puts `$set` and
 * `$set_once` (initial URL, referrer, entry path) on the event itself, not in
 * `properties`, so those are scrubbed too, and so is an exception's text.
 */
export function beforeSend(
  event: CaptureResult | null,
  app: string,
  environment: string,
): CaptureResult | null {
  if (!event) return event;
  const props: Record<string, unknown> = event.properties ?? {};
  sanitizeProperties(props);
  for (const block of [event.$set, event.$set_once]) {
    if (block && typeof block === "object") sanitizeProperties(block as Record<string, unknown>);
  }
  scrubException(props);
  props["app"] = app;
  props["environment"] = environment;
  event.properties = props as CaptureResult["properties"];
  return event;
}

export function posthogOptions(host: string, app: string, environment: string): Partial<PostHogConfig> {
  return {
    api_host: host,
    defaults: "2026-05-30",
    // A profile only for the person we identify.
    person_profiles: "identified_only",
    // One project and one key across Carbon apps: the default cookie on the
    // parent domain would be shared by all of them, and so would the identity.
    cross_subdomain_cookie: false,
    // Sent by startAnalytics once the person is identified, then by
    // trackPageview per path: posthog-js's own would count every query change.
    capture_pageview: false,

    // Each set in code: anything left undefined falls back to the shared
    // project's remote config, which another app's owner controls.
    autocapture: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    rageclick: false,
    // An object, not `true`, so the shared project's setting is never consulted.
    // console.error stays off: console text can quote anything on screen.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // Attribution is off: its targets are CSS selectors of what was on screen.
    capture_performance: {
      web_vitals: true,
      network_timing: false,
      web_vitals_attribution: false,
    },
    logs: { captureConsoleLogs: false },
    enable_recording_console_log: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },

    // Analytics only: no flags, surveys, experiments, tours or chat.
    advanced_disable_flags: true,
    disable_surveys: true,
    disable_web_experiments: true,
    disable_product_tours: true,
    disable_conversations: true,

    before_send: (event) => beforeSend(event, app, environment),
  };
}

/** The slice of the posthog-js client the identity sync needs. */
export interface IdentityClient {
  get_distinct_id(): string;
  reset(): void;
  identify(id: string, props: Record<string, unknown>): void;
}

/**
 * Sign-out does not clear the stored distinct_id, so on a shared browser
 * identifying the next person would merge them into the last. Reset whenever
 * the stored id is not this person's, which also drops the anonymous id.
 */
export function syncIdentity(client: IdentityClient, distinctId: string, email: string): void {
  if (client.get_distinct_id() !== distinctId) client.reset();
  client.identify(distinctId, { email });
}

// Module scope, not component state: StrictMode runs effects twice, and a
// second init() would register every listener again.
let started = false;
// The path the last `$pageview` was for, normalized, so a query change, a
// trailing slash or StrictMode's second effect run never counts a page twice.
let lastPageviewPath: string | null = null;

export type Page = "my_tools" | "training" | "handbook" | "handbook_document" | "sign_in" | "sign_out";

// The router matches paths case-insensitively and with or without a trailing
// slash, so `/Training/` and `/training` are the same page.
function normalizePath(pathname: string): string {
  return pathname.toLowerCase().replace(/\/+$/, "") || "/";
}

/** The page a path shows, by name, never echoing the path; null for one that only redirects. */
export function pageOf(pathname: string): Page | null {
  const path = normalizePath(pathname);
  if (path === "/") return "my_tools";
  if (path === "/training") return "training";
  if (path === "/handbook") return "handbook";
  if (/^\/handbook\/[^/]+$/.test(path)) return "handbook_document";
  if (path === "/sign-in") return "sign_in";
  if (path === "/sign-out" || path === "/signed-out") return "sign_out";
  return null;
}

/**
 * One `$pageview` per path the person goes to. Never queued: posthog-js reads
 * the URL when it captures, so a late one would name the wrong page.
 */
export function trackPageview(pathname: string): void {
  const page = pageOf(pathname);
  const path = normalizePath(pathname);
  if (!started || page === null || path === lastPageviewPath) return;
  lastPageviewPath = path;
  try {
    posthog.capture("$pageview", { page });
  } catch {
    // The page matters; counting it does not.
  }
}

/** Starts PostHog once per page load. Does nothing without settings, and never throws. */
export function startAnalytics(config: BrowserAnalytics | null, email: string): void {
  if (started || !config) return;
  try {
    posthog.init(config.key, posthogOptions(config.host, config.app, config.environment));
    started = true;
    syncIdentity(posthog, config.distinctId, email);
    // Wherever the person is by now: a page they left before this is not counted,
    // and a path that only redirects leaves the pageview to where it lands.
    trackPageview(window.location.pathname);
    for (const [event, props] of pending) posthog.capture(event, props);
    pending = [];
  } catch {
    // Analytics must never break the page.
  }
}

/** One tool opened. Its key, name and kind; never its URL. */
export function trackToolClick(tool: Tool, via: "card" | "filter_enter"): void {
  if (!started) return;
  try {
    posthog.capture("tool_clicked", {
      tool_key: tool.key,
      tool_name: tool.name,
      tool_kind: tool.kind,
      via,
    });
  } catch {
    // Opening the tool matters; counting it does not.
  }
}

/** What a Try again was for. */
export type RetryArea =
  | "video"
  | "process"
  | "quiz"
  | "quiz_answer"
  | "training"
  | "handbook_document"
  | "handbook_library";

/** What a Sign in again was for. */
export type SignInArea = "video" | "training" | "handbook_ask";

/** The shell's, My tools' and every recovery button's events: enums, counts and a tool key. */
export interface AppEvents {
  /** The person switched the theme; applying the saved one is not a change. */
  theme_changed: { theme: Theme };
  /** Chosen in the account menu, before the confirmation page. */
  sign_out_clicked: { via: "sign_out" | "switch_account" };
  /** The filter settled for a second on text it had not settled on before. Never the text. */
  tools_filtered: { visible_count: number; total_count: number };
  /** `tool_key` only for the card with no link. */
  it_ticket_clicked: { via: "missing_tool" | "no_tools" | "tool_unavailable"; tool_key?: string };
  retry_clicked: { area: RetryArea };
  signin_again_clicked: { area: SignInArea };
}

/** How a track player step shows: an uploaded video, a file, a process, a link video, a quiz, or nothing to open. */
export type TrainingStepKind = "video" | "file" | "process" | "link_video" | "quiz" | "unavailable";

/** Every training event and its properties: ids, a kind and counts, never a URL, a title or a quiz's words. */
export interface TrainingEvents {
  training_viewed: { track_count: number; items_done: number; items_total: number };
  training_item_opened: { material_id: string; track_id: string; kind: "file" | "video" | "process" };
  training_item_completed: { material_id: string; track_id: string };
  training_item_uncompleted: { material_id: string; track_id: string };
  training_track_completed: { track_id: string; item_count: number };
  training_nudge_clicked: { track_id: string };
  /** `resumed` when the attempt already had answers or was open. */
  training_quiz_started: { material_id: string; track_id: string; resumed: boolean; answered: number; total: number };
  /** `attempt` is which attempt this was, counting this one. */
  training_quiz_submitted: {
    material_id: string;
    track_id: string;
    passed: boolean;
    percent: number;
    correct: number;
    total: number;
    attempt: number;
  };
  /** One per failed save; `status` is 0 when the request never arrived. */
  training_quiz_failed_to_save: { material_id: string; status: number };
  /** An uploaded video's player opened; `resumed` when some of it was already watched. */
  training_video_opened: { material_id: string; track_id: string; resumed: boolean };
  /** The first progress answer that marked a video done. */
  training_video_completed: { material_id: string; track_id: string };
  /**
   * A video that would not play, or a progress report thrown away. `code` is
   * carbon-os's or Gatekeeper's (`implausible_progress`, `storage_unavailable`,
   * `media_error` once the player gives up…), `status` 0 when none. Never a URL.
   */
  training_video_error: { material_id: string; code: string | null; status: number };
  /** The track player opened, once per opening, on the step it opened at. */
  training_player_opened: {
    track_id: string;
    material_id: string;
    step_kind: TrainingStepKind;
    via: "start" | "continue" | "row" | "quiz_row" | "link";
  };
  /** A step the person picked; the player moving on by itself sends nothing. */
  training_step_selected: {
    track_id: string;
    material_id: string;
    step_kind: TrainingStepKind;
    via: "sidebar" | "next_button";
  };
  /** The track player closed, `duration_ms` after it opened. */
  training_player_closed: { track_id: string; duration_ms: number };
  /** One per "I have read this version" answered. A request that never arrived is `error`. */
  training_process_acknowledged: {
    material_id: string;
    track_id: string;
    outcome: "saved" | "track_expired" | "version_changed" | "error";
  };
  /**
   * The quiz closed by its Close button or its sheet. `answered` and `total` are
   * the attempt's while answering, the graded questions on a result, and the
   * row's summary before an attempt loaded. Closing the player mid-quiz sends
   * only `training_player_closed`.
   */
  training_quiz_closed: {
    material_id: string;
    track_id: string;
    stage: "loading" | "answering" | "result" | "unavailable";
    answered: number;
    total: number;
  };
}

// Training, the handbook and a click can come before the tool list starts PostHog;
// what they report in the meantime waits, and is dropped with the page if PostHog never starts.
const PENDING_LIMIT = 20;
let pending: [string, Record<string, unknown>][] = [];

function captureOrQueue(event: string, props: Record<string, unknown>): void {
  if (!started) {
    if (pending.length < PENDING_LIMIT) pending.push([event, { ...props }]);
    return;
  }
  try {
    posthog.capture(event, props);
  } catch {
    // The page matters; counting it does not.
  }
}

export function trackApp<E extends keyof AppEvents>(event: E, props: AppEvents[E]): void {
  captureOrQueue(event, props);
}

export function trackTraining<E extends keyof TrainingEvents>(event: E, props: TrainingEvents[E]): void {
  captureOrQueue(event, props);
}

type HandbookTab = "ask" | "browse";

/** How a document was reached: from a list, the answer, a link in another document, a book, or `direct` (typed, bookmarked or reloaded). */
export type HandbookOpenVia =
  | "updated_list"
  | "recent_list"
  | "search_result"
  | "folder_list"
  | "citation"
  | "related_passage"
  | "document_link"
  | "book_start"
  | "book_next"
  | "book_previous"
  | "direct";

/**
 * Every handbook event and its properties: outcomes, counts, a duration, a
 * version and document and folder ids. Never the question, the answer, a
 * passage, a search, a title or a folder's name.
 */
export interface HandbookEvents {
  /**
   * One per question that reached the server, once its result shows. Timeouts,
   * network failures and "unavailable" are `error`; `source_count` is the
   * passages shown, cited or not, 0 for `rate_limited` and `error`.
   */
  handbook_question_asked: {
    outcome: HandbookAskResponse["outcome"] | "rate_limited" | "error";
    source_count: number;
    duration_ms: number;
    /** Asked again from Try again, rather than typed and sent. */
    retry: boolean;
  };
  /** An inline `[n]` or a numbered source opened; `position` is n. */
  handbook_citation_opened: { document_id: string; position: number };
  /** A related or matching passage's document opened; `position` counts from 1. */
  handbook_passage_opened: { document_id: string; position: number; kind: "related" | "matching" };
  /** Clear, with the outcome that was showing. */
  handbook_answer_cleared: {
    outcome: HandbookAskResponse["outcome"] | "rate_limited" | "error" | "unavailable" | "needs_signin" | "signin_failed";
  };
  /** A Browse or Ask tab clicked; the tab a link or the URL picks is not. */
  handbook_tab_changed: { tab: HandbookTab; from: HandbookTab };
  /** The title search settled for a second on text it had not settled on before. Never the text. */
  handbook_searched: { result_count: number; document_count: number };
  /** `folder_id` is `unfiled`, or null for the handbook's home and a folder this person can't see. */
  handbook_folder_opened: { folder_id: string | null; via: "sidebar" | "card" | "breadcrumb" | "back" };
  /**
   * A document's first load in the reader, or the load after its Try again.
   * `position` is its row in a list or its citation number; the book's are null
   * outside "Read this folder".
   */
  handbook_document_opened: {
    document_id: string;
    via: HandbookOpenVia;
    position: number | null;
    in_book: boolean;
    book_position: number | null;
    book_page_count: number | null;
    updated_since_read: boolean;
    outcome: "shown" | "not_available" | "error";
  };
  /** Finish at the end of "Read this folder". */
  handbook_book_finished: { folder_id: string; page_count: number };
  /** "I've read this version" answered. `reread` when a lower version had been read before. */
  handbook_document_marked_read: {
    document_id: string;
    version: number;
    reread: boolean;
    in_book: boolean;
    outcome: "saved" | "newer_version" | "error";
  };
}

export function trackHandbook<E extends keyof HandbookEvents>(event: E, props: HandbookEvents[E]): void {
  captureOrQueue(event, props);
}

/** Forgets the person on sign-out, so the next one on this browser starts clean. */
export function resetAnalytics(): void {
  // Nothing waiting is ever sent under the next person's id.
  pending = [];
  if (!started) return;
  try {
    posthog.reset();
  } catch {
    // Sign-out goes ahead regardless.
  }
}
